import {toast} from '@wandb/components';
import {active} from 'd3';
import React, {useContext, useRef, useState} from 'react';

import {useActiveThread} from '../../agentChat/store/context';
import {useDismissibleNotice} from '../../agentChat/useDismissibleNotice';
import {useUnseenSettledThread} from '../../agentChat/useUnseenSettledThread';
import {trackNavRightFeatureOpened} from '../../services/analytics/productEvents';
import {
  useIsAdminModeActiveAndIsNotLocalEnv,
  viewingAs,
} from '../../util/admin';
import {
  AgentChatProvider,
  useAgentChatAvailability,
  useWBAgentController,
} from '../AgentChat';
import {AgentChatRuntimeBoundary} from '../AgentChat/AgentChatProvider';
import {AccountSelectorContext} from '../Search/SearchNav/AccountSelectorContextProvider';
import {ThreadHistoryProvider} from './chatHistory/ThreadHistoryProvider';
import {useTrackWBAgentPanel} from './useTrackWBAgent';
import {WBAgentChatButton} from './WBAgentChatButton';
import {WBAgentChatDrawer} from './WBAgentChatDrawer';
import {WBAgentChatWindow} from './WBAgentChatWindow';
import {WBAgentRunDisplayNameProvider} from './WBAgentRunDisplayNameProvider';
import {WBAgentSessionRestore} from './WBAgentSessionRestore';
import {useWBAgentWindowStore} from './window/useWBAgentWindowStore';

/**
 * Component tree:
 * WBAgent → WBAgentInner
 *   ├── WBAgentChatButton
 *   ├── WBAgentChatWindow (floating, undocked)
 *   │   ├── WBAgentHeader
 *   │   └── WBAgentThread
 *   │       ├── WBAgentMessageAssistant
 *   │       ├── WBAgentMessageUser
 *   │       └── WBAgentComposer
 *   │           └── WBAgentEditor
 *   └── WBAgentChatDrawer (docked)
 *       ├── WBAgentHeader
 *       └── WBAgentThread
 *           └── ...
 */

export const WBAgent = () => {
  return (
    <AgentChatProvider>
      <WBAgentInner />
    </AgentChatProvider>
  );
};

export const WBAgentInner = () => {
  const {isAvailable: isWorkspaceAgentEnabled} = useAgentChatAvailability();
  const activeThread = useActiveThread();
  const open = useWBAgentWindowStore(s => s.open);
  const setOpen = useWBAgentWindowStore(s => s.setOpen);
  const docked = useWBAgentWindowStore(s => s.docked);
  const {selectedAccount} = useContext(AccountSelectorContext);
  const isAdminMode =
    useIsAdminModeActiveAndIsNotLocalEnv() || viewingAs() !== '';
  const {dismissed: introDismissed, dismiss: dismissIntro} =
    useDismissibleNotice('wbagent-intro');
  const {trackOpened, trackClosed, trackNavbarButtonViewed} =
    useTrackWBAgentPanel();
  const {openChatToThread} = useWBAgentController();

  const activeToastRef = useRef<{rootTurnId: string; key: string} | null>(null);
  // Handles notifying user via toast that a chat completed while the agent was minimized.
  // The callback only fires for live completions --> the toast won't re-appear
  // when the persisted value is rehydrated after a page reload.
  const unseenSettledThread = useUnseenSettledThread(
    open,
    thread => {
      // Only ever show toast for the most recently completed thread
      const prev = activeToastRef.current;
      if (prev != null) {
        toast.dismiss(prev.key);
      }
      const key = toast.success('New response', {
        action: {
          label: 'Go to chat',
          onPress: () => {
            toast.dismiss(key);
            openChatToThread(thread.rootTurnId);
          },
        },
      });
      activeToastRef.current = {rootTurnId: thread.rootTurnId, key};
    },
    thread => {
      // onSeen func for dismissing toast
      const active = activeToastRef.current;
      if (active != null && active.rootTurnId === thread.rootTurnId) {
        toast.dismiss(active.key);
        activeToastRef.current = null;
      }
    }
  );

  // Show the onboarding cue at most once per session, but not when a thread
  // restores open on load. Snapshot so recording the dismissal on impression
  // doesn't hide the cue mid-session.
  //
  // NOTE: `open` is false at first render (restore runs after), so check the
  // persisted restore target, which is hydrated synchronously.
  const [showCue, setShowCue] = useState(
    () =>
      !introDismissed &&
      useWBAgentWindowStore.getState().restoreThreadId == null
  );

  return (
    <>
      {isWorkspaceAgentEnabled && (
        <WBAgentChatButton
          showOnboardingCue={showCue}
          unseenSettledThread={unseenSettledThread}
          onImpression={() => {
            trackNavbarButtonViewed();
            // NOTE: the button only mounts when the agent is available, so
            // recording here (not at WBAgentInner mount) avoids pre-dismissing
            // the cue for users who never see it.
            if (!introDismissed) {
              dismissIntro();
            }
          }}
          onPress={() => {
            if (!open) {
              trackNavRightFeatureOpened({
                feature: 'agent',
                selected_account: selectedAccount,
                is_admin_mode: isAdminMode,
              });
              trackOpened();
            } else {
              trackClosed();
            }
            setShowCue(false);
            setOpen(!open);
          }}
          isLoading={activeThread?.status === 'running'}
        />
      )}
      <AgentChatRuntimeBoundary>
        <WBAgentSessionRestore />
        {open && (
          <WBAgentRunDisplayNameProvider>
            <ThreadHistoryProvider>
              {docked ? <WBAgentChatDrawer /> : <WBAgentChatWindow />}
            </ThreadHistoryProvider>
          </WBAgentRunDisplayNameProvider>
        )}
      </AgentChatRuntimeBoundary>
    </>
  );
};
